// libs
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

// defs
import { IDetailState } from 'shared/store/details/types';
import { DEFAULT_VIDEO_CONFIG } from 'shared/layouts/DetailsPage/components/ProductImage/components/Image/components/VideoPlayer/helpers';
import { getSelectedSKUFaq } from 'shared/store/details/selectors';

/**
 * Get Product - Explore Feed Image
 * @returns {ProductDetail.IProductMediaImage[]}
 */
export const useProductExploreImages = () =>
  useSelector((state: { details: IDetailState }) => state.details.exploreImages);

/**
 * Get Product Videos
 * @returns {ProductDetail.ProductVideo[]}
 */
export const useProductVideos = () =>
  useSelector(
    (state: { details: IDetailState }) => state.details?.skuData?.product?.videoMediaSources
  ) || [];

/**
 * Check if we can show size selector
 * @returns {boolean}
 */
export const useIsShowSizeSelector = () =>
  useSelector((state: { details: IDetailState }) => state.details.showSizeSelector);

/**
 * @typedef VideoConfig
 * @type {object}
 * @property {boolean} videoEnabled - Is Video feature enabled.
 * @property {number} videoPosition - Position at which Video should be shown in image gallery.
 */

/**
 * Get Selected SKU title
 * @returns {string | undefined}
 */
export const useSelectedSKUTitle = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.title || state.details?.skuData?.product?.title || ''
  );

/**
 * Get Selected SKU title
 * @returns {string | undefined}
 */
export const useSelectedSKUCode = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.sku || state.details?.skuData?.product?.sku || ''
  );

/**
 * Get Selected SKU sub title
 * @returns {string | undefined}
 */
export const useSelectedSKUSubTitle = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.subTitle || state.details?.skuData?.product?.subTitle
  );

/**
 * Get Selected SKU Images
 * @returns {(ProductDetail.IProductMediaImage | ProductDetail.IProductMediaVideo)[] | undefined}
 */
export const useSelectedSKUImages = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.productMedia || state.details?.skuData?.product?.productMedia
  );

/**
 * Get Selected SKU Price
 * @returns {number | undefined}
 */
export const useSelectedSKUPrice = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.price || state.details?.skuData?.product?.price
  );

/**
 * Get Selected SKU Discount
 * @returns {number | undefined}
 */
export const useSelectedSKUDiscount = () =>
  useSelector((state: { details: IDetailState }) => {
    // assume parent sku discount only when any child is not selected
    if (isEmpty(state.details.selectedSKU)) return state.details.skuData?.product.discount;

    // return selected sku discount, be it 0 or falsy
    return state.details?.selectedSKU?.discount;
  });

/**
 * Get Selected SKU Discount Price
 * @returns {number | undefined}
 */
export const useSelectedSKUDiscountPrice = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.discountedPrice ||
      state.details?.skuData?.product?.discountedPrice
  );

/**
 * Get Selected SKU Size Guide URL
 * @returns {string | undefined}
 */
export const useSelectedSKUSizeGuide = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.sizeGuideURL || state.details?.skuData?.product?.sizeGuideURL
  );

/**
* Get Size Inventory Nudge text
* @returns {string | undefined}
*/
export const useSelectedSizeInventoryNudge = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.inventoryNudge || state.details?.skuData?.product?.inventoryNudge
  );

/**
 * Is selected SKU out of stock
 * @returns {boolean}
 */
export const useIsSelectedSKUOOS = () =>
  useSelector((state: { details: IDetailState }) =>
    Boolean(
      state.details?.selectedSKU?.isOutOfStock || state.details?.skuData?.product?.isOutOfStock
    )
  );

/**
 * Get Selected SKU ID
 * @returns {string | undefined}
 */
export const useSelectedSKUID = () =>
  useSelector((state: { details: IDetailState }) => Boolean(state.details?.selectedSKUId));

/**
 * Get Selected SKU
 * @returns {ProductDetail.ISizeOption | undefined}
 */
export const useSelectedSKU = () =>
  useSelector((state: { details: IDetailState }) => state.details?.selectedSKU);

/**
 * Get Selected SKU Medias (images / videos)
 * @returns {(ProductDetail.IProductMediaImage | ProductDetail.IProductMediaVideo | ProductDetail.ProductVideo)[]}
 */
export const useSelectedSKUMedias = () => {
  const images = useSelectedSKUImages() || [];
  const exploreImages = useProductExploreImages();
  const videos = useProductVideos();
  const { position: videoPosition } = DEFAULT_VIDEO_CONFIG;

  return useMemo(() => {
    // merge product images and explore images
    let media = [...images, ...exploreImages];

    // insert video in given position
    return [...media.slice(0, videoPosition - 1), ...videos, ...media.slice(videoPosition - 1)];
  }, [exploreImages, images, videoPosition, videos]);
};

/**
 * Get Selected SKU Info
 * @returns {ProductDetail.IProductInfo[] | undefined}
 */
export const useSelectedSKUInfo = () =>
  useSelector((state: { details: IDetailState }) => state.details?.skuData?.product?.productInfo);

/**
 * Get Selected Meta Data
 * @returns {ProductDetail.IMetaData | undefined}
 */
export const useSelectedSKUMetaData = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.meta_data || state.details?.skuData?.product?.meta_data
  );

/**
 * Get selected sku customisation info
 * @return {ProductDetail.ICustomizationForm | undefined}
 */
export const useSelectedSKUCustomisationInfo = () =>
  useSelector(
    (state: { details: IDetailState }) => state?.details?.selectedSKU?.customization_form
  );

/**
 * Get customisation terms and condition
 * @return {string}
 */
export const useCustomisationTNC = () =>
  useSelector(
    (state: { details: IDetailState }) => state?.details?.selectedSKU?.tnc_personalisation
  );

/**
 * Get Selected SKU Price Reveal data
 * @returns {ProductDetail.IPriceRevealData | undefined}
 */
export const useSelectedSKUPriceRevealData = () =>
  useSelector((state: { details: IDetailState }) => {
    if (state.details?.selectedSKU) {
      return state.details?.selectedSKU?.price_reveal_data;
    }
    return state.details?.skuData?.product?.price_reveal_data;
  });

export const useSelectedSKUSalePriceLiveConfig = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.sale_price_live_flag ||
      state.details?.skuData?.product?.sale_price_live_flag ||
      false
  );


/**
* Check if we can show isSaleParticipating
* @returns {boolean}
*/
export const useSelectedSKUSaleParticipating = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.is_sale_participating ||
      state.details?.skuData?.product?.is_sale_participating
  );


export const useSelectedSKUSpecialTag = () =>
  useSelector(
    (state: { details: IDetailState }) =>
      state.details?.selectedSKU?.specialTag ||
      state.details?.skuData?.product?.specialTag
  );

/**
 * FAQ from selected SKU Know Your Product widget
 */
export const useSelectedSKUFaq = () => useSelector(getSelectedSKUFaq);

